import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  DossierDtoEchaResponseStatusEnum,
  LoadingInfo,
  OrganizationDto,
  OrganizationGroupDto,
  PageProductSearchResultDto,
  ProductSearchResultDto,
  ReportRequestDtoReportTypeEnum
} from '@scip/api';
import { SearchControllerService } from '@scip/api';
import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { forkJoin, of, ReplaySubject } from 'rxjs';
import { retry, takeUntil } from 'rxjs/operators';

import {
  DossierUtils,
  DownloadConfig,
  DownloadType,
  EnumUtils,
  Feature,
  ProductionInEuType,
  RequestedType,
  SubmissionEmptyStatus,
  SubmissionStatus,
  SubmissionStatusWithEmpty,
  translationKeys
} from '../../../../core';
import {
  BackendErrorService,
  FeatureService,
  OrganizationGroupService,
  ProductTableService,
  ProfileService,
  SearchService,
  UiSettingsIdentifier
} from '../../../../services';
import { AvanoLoadingIndicatorService } from '@avano/avano-components';
import { DossierError } from '../../../../shared';
import { TableColumnConfiguration, TableConfiguration } from '@avano/avano-components/avano-table';
import { ReportService } from '../../../../services/report.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { ProductsPageActions } from '../../../../store/products-page';
import { isEqual } from 'lodash-es';
import { columnKeys, filterStateForSubmissions, filterStateForProductionInEU, getTableColumns, tableConfiguration } from './products-table.config';

@Component({
  selector: 'scip-products-table',
  templateUrl: './products-table.component.html',
  styleUrls: ['./products-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductsTableComponent implements OnInit, OnDestroy {
  @ViewChild('productsTable') productsTable: Table;

  @Input()
  set loadingInfo(val: LoadingInfo) {
    if (val && !isEqual(val, this._loadingInfo)) {
      this._loadingInfo = val;
      this.loadMoreProducts();
    }
  }
  get loadingInfo(): LoadingInfo {
    return this._loadingInfo;
  }

  get filters(): Pick<LoadingInfo, 'filters'> {
    return { ...this._loadingInfo?.filters };
  }

  totalProductsCount = 0; // Initialize the count so that it show 0 while loading instead of undefined
  firstOffSet = 0; // Initialize with 0 as we start from page 1

  // Parts of our table configuration are loaded asynchronous (list of organizations for the column filter)
  // So we need to wait until the configuration is ready before displaying the table
  tableConfigurationInitialized = false;

  products: ProductSearchResultDto[];

  columnConfig: TableColumnConfiguration[];
  // To have a typesafe definition of the fields we are accessing from the dto within the table
  columnKeys = columnKeys;

  rowsCount = 50;

  selectedProducts: ProductSearchResultDto[];

  tableConfiguration: TableConfiguration = tableConfiguration;

  emptyMessage = translationKeys.AvanoTable.EmptyMessage.noRecords;

  blockingIdentifier = 'productTable';

  isTriggeredFromFilterChangeOrNewSearch = false;

  latestSearchId: number;
  displayExportDialog = false;

  showDossierErrorDetails = false;
  showOrganizationErrorDetails = false;
  selectedDossierError: DossierError;
  selectedOrgError: DossierError;
  submissionStatus: typeof SubmissionStatus = SubmissionStatus;
  translationKeys: typeof translationKeys = translationKeys;

  // Subscriptions on observables are not automatically tied to the lifecycle of a component.
  // So to destroy the subscriptions when a component gets destroyed we need to manage it manually or
  // add a dependency to be able to use some predefined pipe-operators (there is currently a reason i
  // did not add this dependency, because it needs a bigger rework which can be done again after we upgraded
  // the angular version)
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  // We need to trigger the update of the list from the outside, therefore we need to replay the
  // last lazyLoadEvent as we otherwise do not know the current page, how much entries are displayed on the page,...
  private lazyLoadEvent: LazyLoadEvent;
  private _loadingInfo: LoadingInfo;

  constructor(
    private productTableService: ProductTableService,
    private searchService: SearchService,
    private reportService: ReportService,
    private backendErrorService: BackendErrorService,
    private translateService: TranslateService,
    private profileService: ProfileService,
    private organizationGroups: OrganizationGroupService,
    private featureService: FeatureService,
    private changeDet: ChangeDetectorRef,
    private avanoLoadingIndicatorService: AvanoLoadingIndicatorService,
    private searchControllerService: SearchControllerService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.initializeTable();
    this.setupListener();
  }

  ngOnDestroy(): void {
    // User navigates away from this component, therefore discard the selection in the service
    this.resetSelection();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  showExportDialog(): void {
    this.displayExportDialog = true;
  }

  downloadReport({ downloadType }: DownloadConfig): void {
    const searchInfo: LoadingInfo = { ...this.loadingInfo };
    if (downloadType === DownloadType.AllResults) {
      searchInfo.rows = this.totalProductsCount;
    }
    this.reportService.triggerReportCreation(ReportRequestDtoReportTypeEnum.ProductsDownload, searchInfo);
    this.displayExportDialog = false;
  }

  closeDownloadDialog(): void {
    this.displayExportDialog = false;
  }

  rowsChanged(): void {
    this.productTableService.setProducts(this.selectedProducts);
  }

  triggerColumnFilter(col: TableColumnConfiguration, event: any): void {
    // No matter which filter was triggered or even removed, we should start from the first page
    this.isTriggeredFromFilterChangeOrNewSearch = true;

    // For some columns we have a different mapping
    const filterField = col.filterConfiguration?.filterField ? col.filterConfiguration.filterField : col.field;

    if (filterField === this.columnKeys.organizationName) {
      const orgName = (event as OrganizationDto)?.name ? event.name : '';
      this.productsTable.filter(orgName, filterField, 'contains');
    } else if (filterField === this.columnKeys.scipDossierSubmissionStatus) {
      this.productsTable.filter((event as string[]).join(','), filterField, 'includes');
    } else {
      this.productsTable.filter(event, filterField, 'contains');
    }
    this.productsTable.saveState();
  }

  resetFilters(): void {
    this.store.dispatch(ProductsPageActions.resetFilters());
    this.productsTable.saveState();
  }

  refreshTable(): void {
    this.loadMoreProducts();
  }

  lazyLoad(event: LazyLoadEvent): void {
    if (this.loadingInfo?.first !== event.first || this.loadingInfo.sortField !== event.sortField || this.loadingInfo.sortOrder !== event.sortOrder) {
      this.store.dispatch(
        ProductsPageActions.setTableState({
          tableState: {
            ...this.loadingInfo,
            first: event.first,
            sortField: event.sortField,
            sortOrder: event.sortOrder,
            filters: event.filters
          }
        })
      );
    }
  }

  loadMoreProducts(): void {
    // We will start querying the backend soon, therefore "block" the table with a spinner
    this.avanoLoadingIndicatorService.block([this.blockingIdentifier]);
    this.emptyMessage = '';

    // User e.g. changes the page of the table -> discard the selected products
    this.resetSelection();

    this.searchControllerService.getProductSearch(this.loadingInfo).subscribe(
      (result: PageProductSearchResultDto) => this.handleSearchResult(result),
      (err) => this.handleSearchError(err)
    );
  }

  showErrorDetails(rowData: ProductSearchResultDto): void {
    this.selectedDossierError = {
      echaResponseErrorText: rowData.echaResponseErrorText,
      echaResponseStatus: rowData.echaResponseStatus
    };
    this.showDossierErrorDetails = true;
  }

  showOrgErrorDetails(rowData: ProductSearchResultDto): void {
    this.selectedOrgError = {
      echaResponseErrorText: rowData.validationErrors,
      echaResponseStatus: rowData.echaResponseStatus
    };
    this.showOrganizationErrorDetails = true;
  }

  private setupListener(): void {
    // This event will only fire when there is a new(!) search triggered.
    // No matter if the user is coming with a changed/new searchterm or with an
    // empty one. But for pagination the loadMoreProducts-method is called.
    this.searchService
      .getProductSearchEvents()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((search) => {
        this.latestSearchId = search.searchId;
        this.store.dispatch(
          ProductsPageActions.setLatestSearchId({
            searchId: search.searchId
          })
        );

        if (this.lazyLoadEvent) {
          this.loadMoreProducts();
        }
        this.changeDet.markForCheck();
      });

    // Listen to the event to refresh products table (triggered after editing a product is done)
    this.productTableService.refreshProductsTable.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.loadMoreProducts();
      this.changeDet.markForCheck();
    });

    // Listen to changes of the selection (can happen from the outside)
    this.productTableService.selectionChanged.pipe(takeUntil(this.destroyed$)).subscribe((newlySelectedProducts) => {
      this.selectedProducts = newlySelectedProducts;
      this.changeDet.markForCheck();
    });
  }

  private handleSearchResult(searchResult: PageProductSearchResultDto): void {
    this.products = searchResult.content;
    this.totalProductsCount = searchResult.totalElements;
    this.firstOffSet = searchResult.pageable.offset;
    this.avanoLoadingIndicatorService.unblock([this.blockingIdentifier]);
    if (this.loadingInfo.filters) {
      this.emptyMessage = translationKeys.AvanoTable.EmptyMessage.emptySearch;
    } else {
      this.emptyMessage = translationKeys.AvanoTable.EmptyMessage.noRecords;
    }
    this.changeDet.markForCheck();
  }

  private handleSearchError(err: HttpErrorResponse): void {
    this.backendErrorService.handleError(err, translationKeys.productsTableComponent.typescript.msg.productsLoadingError);
    this.avanoLoadingIndicatorService.unblock([this.blockingIdentifier]);
  }

  private resetSelection() {
    this.productTableService.setProducts([]);
  }

  private initializeTable(): void {
    const allOrganizationsCall = this.profileService.getAllOrganizations(true, false, true).pipe(retry(1));
    const allOrganizationGroupsCall = this.featureService.isActive(Feature.LE_GROUPING)
      ? this.organizationGroups.getAllOrganizationGroups(true)
      : of(undefined);

    forkJoin([allOrganizationsCall, allOrganizationGroupsCall]).subscribe(
      ([allActiveOrganizationsSorted, organizationGroups]: [OrganizationDto[], OrganizationGroupDto[]]) => {
        this.columnConfig = getTableColumns(
          this.getSubmissionStatusFilterTranslations(),
          this.getProductionInEUFilterTranslations(),
          allActiveOrganizationsSorted,
          organizationGroups
        );
        this.tableConfigurationInitialized = true;
        this.changeDet.markForCheck();
      }
    );
  }

  private getSubmissionStatusFilterTranslations(): SelectItem[] {
    const translatedStatus = this.translateService.instant(filterStateForSubmissions.map((state: SubmissionStatus) => DossierUtils.getTranslationKey(state)));

    return filterStateForSubmissions
      .map((state: string) => {
        const statusConvertedToPrimeNGSelectOption: SelectItem = {
          label: translatedStatus[DossierUtils.getTranslationKey(state)],
          value: state
        };
        return statusConvertedToPrimeNGSelectOption;
      })
      .sort((statusA: SelectItem, statusB: SelectItem) => {
        return statusA.label > statusB.label ? 1 : statusB.label > statusA.label ? -1 : 0;
      });
  }

  private getProductionInEUFilterTranslations(): SelectItem[] {
    const translatedProductionInEU = this.translateService.instant(
      filterStateForProductionInEU.map((state: ProductionInEuType) => EnumUtils.getProductionInEUTranslationKey(state))
    );

    return filterStateForProductionInEU.map((state: string) => {
      const productionInEUConvertedToPrimeNGSelectOption: SelectItem = {
        label: translatedProductionInEU[EnumUtils.getProductionInEUTranslationKey(state)],
        value: state
      };
      return productionInEUConvertedToPrimeNGSelectOption;
    });
  }
}
